import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMessageSquare, FiAlertCircle, FiFilter, FiArrowLeft } from 'react-icons/fi';
import { getMenteeTickets } from '../../services/mentorService';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [filteredTickets, setFilteredTickets] = useState([]);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);
        const response = await getMenteeTickets();
        setTickets(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets. Please try again later.');
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredTickets(tickets);
    } else {
      const filtered = tickets.filter(ticket => ticket.status === filter);
      setFilteredTickets(filtered);
    }
  }, [filter, tickets]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      default:
        return (typeof status === 'string') ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/mentor/dashboard"
          className="inline-flex items-center text-primary-600 hover:text-primary-900"
        >
          <FiArrowLeft className="mr-2" /> Back to Dashboard
        </Link>
      </div>

      <PageHeader
        title={pageDescriptions['/mentor/tickets'].title}
        description={pageDescriptions['/mentor/tickets'].description}
        whatYouCanDo={pageDescriptions['/mentor/tickets'].whatYouCanDo}
        whatYouShouldDo={pageDescriptions['/mentor/tickets'].whatYouShouldDo}
        actions={
          <div className="relative inline-block text-left">
          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="all">All Tickets</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
        }
      />

      {/* Tickets List */}
      <div className="card overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => (
              <li key={ticket.id}>
                <Link to={`/mentor/tickets/${ticket.id}`} className="block hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <FiMessageSquare className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-600">{ticket.subject}</div>
                          <div className="text-sm text-gray-500">
                            From: {ticket.userName} ({ticket.userEmail})
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(ticket.status)}`}>
                          {formatStatus(ticket.status)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <div className="flex items-center text-sm text-gray-500">
                          <span>Category: {ticket.category}</span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))
          ) : (
            <li className="px-4 py-5 text-center text-gray-500">
              No tickets found matching the selected filter.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default TicketList;